import { InMemoryCache, defaultDataIdFromObject } from "@apollo/client/cache";
import type {
  FieldMergeFunction,
  InMemoryCacheConfig,
  StoreObject,
} from "@apollo/client/cache";

const TypePerson = "Person";
const Membership = "Membership";
const IndividualAllocation = "IndividualAllocation";
const Import = "Import";

const merge: FieldMergeFunction = (_existing, incoming) => incoming;

const mergeShallow: FieldMergeFunction = (existing, incoming) => {
  if (!incoming) {
    return existing;
  }
  return { ...existing, ...incoming };
};

// Light Group payloads (e.g. Membership.group from searchMemberships) can
// return null hierarchy fields; do not let them clobber real values the
// planner/forecast trees are built from.
const keepExistingOnNull: FieldMergeFunction = (existing, incoming) =>
  incoming ?? existing;

const inMemoryCacheConfig = {
  typePolicies: {
    Query: {
      fields: {
        // v4 equivalent of v3 cacheRedirects: resolves getPerson from the in-memory cache
        // when the entity was already loaded as part of a larger query (e.g. team members list),
        // avoiding a redundant network request. Returns undefined to fall back to network.
        getPerson: (_, { args, toReference, canRead }) => {
          if (!args?.["aggregateId"]) return undefined;
          const ref = toReference({
            __typename: TypePerson,
            aggregateId: args["aggregateId"],
          });
          return canRead(ref) ? ref : undefined;
        },
        // Aggregations has no id and its fields are keyed by argument
        // (team(teamId), teams(teamIds)), so without a merge a second team's
        // response replaces the first team's cached aggregates. Each pit is its
        // own cache entry, and the same team+pit key is replaced by newer data.
        // Limits: entries for other teams are kept until evicted, and are only
        // evicted on this client's own structure events (see
        // useEvictAggregationsOnStructureChange), so changes made by other
        // people show after the next fetch. TPP-2106 covers revalidation.
        getAggregations: {
          merge: mergeShallow,
        },
      },
    },
    Tag: {
      fields: {
        tagAttributesMetaData: {
          merge: mergeShallow,
        },
      },
    },
    LinkedStrategy: {
      keyFields: false,
    },
    Group: {
      fields: {
        tags: {
          merge,
        },
        permissions: {
          merge: mergeShallow,
        },
        hierarchyIds: {
          merge: keepExistingOnNull,
        },
        parentIds: {
          merge: keepExistingOnNull,
        },
      },
    },
    ObjectiveAttribute: {
      keyFields: false,
    },
    GroupAttribute: {
      keyFields: false,
    },
    // Permissions objects have no ID — embed them in their parent to avoid
    // merge conflicts when different queries return different permission subsets.
    GroupPermissions: {
      keyFields: false,
    },
    PersonPermissions: {
      keyFields: false,
    },
    Person: {
      fields: {
        memberOf: {
          merge,
        },
        memberships: {
          merge,
        },
        allocations: {
          merge,
        },
        tags: {
          merge,
        },
        accessGroups: {
          merge,
        },
        permissions: {
          merge: mergeShallow,
        },
      },
    },
    Workspace: {
      fields: {
        config: {
          merge: mergeShallow,
        },
      },
    },
  },
  dataIdFromObject: (object: Readonly<StoreObject>) => {
    switch (object.__typename) {
      case Import:
        return `${Import}:${object["id"]}-${object["created"]}`;
      case TypePerson:
        return `${TypePerson}:${object["aggregateId"]}`;
      case IndividualAllocation:
        return `${IndividualAllocation}:${object["allocationId"]}`;
      case Membership: {
        const compositeId = object["currentCompositeId"];
        // Missing key → skip normalization (avoids Membership:undefined collisions).
        if (typeof compositeId !== "string" || !compositeId) {
          return undefined;
        }
        return `${Membership}:${compositeId}`;
      }
      default:
        return defaultDataIdFromObject(object);
    }
  },
} satisfies InMemoryCacheConfig;

/** Fresh cache instance with production type policies (for tests and one-off clients). */
export const createAppCache = () => new InMemoryCache(inMemoryCacheConfig);

const cache = createAppCache();

export default cache;
